import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { easeCustom, hiddenUp, fadeInUp, fadeInUpStagger } from '../presets/animations';
import { initPostsListStagger } from './animations';
gsap.registerPlugin(SplitText);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Post-traite les mots créés par SplitText pour couper après chaque tiret.
 * "Loire-Atlantique" (un seul div) devient "Loire-" + "Atlantique" (deux divs adjacents,
 * sans text node entre eux → pas d'espace visible).
 * Les espaces entre les mots normaux sont conservés (text nodes laissés en place par SplitText).
 */
function splitHyphenatedWords(wordsArray) {
  const result = [];
  [...wordsArray].forEach((wordEl) => {
    const parts = wordEl.textContent.split(/-(?=\S)/);
    if (parts.length === 1) {
      result.push(wordEl);
      return;
    }
    const parent = wordEl.parentNode;
    parts.forEach((part, i) => {
      const newEl = wordEl.cloneNode(false);
      newEl.textContent = i < parts.length - 1 ? part + '-' : part;
      parent.insertBefore(newEl, wordEl);
      result.push(newEl);
    });
    parent.removeChild(wordEl);
  });
  return result;
}

function isInternalLink(link) {
  return (
    link.origin === window.location.origin &&
    !link.hasAttribute('data-no-transition') &&
    link.target !== '_blank' &&
    !link.getAttribute('href').startsWith('#')
  );
}

// ─── Animation de sortie ─────────────────────────────────────────────────────

function playLeaveAnimation() {
  const app = document.querySelector('#app');
  if (!app) return Promise.resolve();
  const contactBtn = document.querySelector('.o-contact__outer');

  const tl = gsap.timeline({ paused: true });

  tl.to('.o-site-header', { opacity: 0, y: -200, duration: 0.6, ease: 'power2.in' });
  tl.to(app, { opacity: 0, y: -200, duration: 0.5, ease: 'power2.in' }, 0);
  tl.to(contactBtn, { opacity: 0, scale: 0, duration: 0.5, ease: 'power2.in' }, 0);

  return tl.play();
}

// ─── Animations d'entrée par vue ─────────────────────────────────────────────

// Helpers partagés entre les vues

function initInterface() {
  gsap.set('.o-site-header', { autoAlpha: 0, y: -24 });
  gsap.set('.o-contact__outer', { opacity: 0, scale: 0 });
}

function appendInterfaceAnimation(tl) {
  tl.to('.o-site-header', { autoAlpha: 1, y: 0, duration: 0.6, ease: easeCustom }, '>-1').to(
    '.o-contact__outer',
    { scale: 1, opacity: 1, duration: 0.6, ease: easeCustom },
    '<'
  );
}

// FRONT-PAGE
function enterFrontPage({ app, mobile }) {
  initInterface();

  const viewportHeight = window.innerHeight;
  const titleSplit = SplitText.create('.o-fp-header__title', { type: 'words' });
  const titleWords = splitHyphenatedWords(titleSplit.words);

  gsap.set('.o-fp-header', {
    clipPath: `inset(0px 0px ${viewportHeight}px 0px round 0px)`,
    autoAlpha: 0,
  });
  gsap.set('.o-fp-header__visual', { scale: 2 });
  gsap.set(titleWords, hiddenUp(40));
  gsap.set('.o-fp-header__desc', hiddenUp());
  gsap.set('.o-fp-header__btn', hiddenUp());

  const tl = gsap.timeline();

  tl.to(app, { opacity: 1, duration: 0.2 })
    .to('.o-fp-header', { autoAlpha: 1, duration: 0.1 })
    .to('.o-fp-header', {
      clipPath: mobile
        ? 'inset(0px 0px 0px 0px round 0px)'
        : 'inset(20px 20px 20px 20px round 10px)',
      duration: 2,
      ease: easeCustom,
    })
    .to('.o-fp-header__visual', { scale: 1, ease: easeCustom, duration: 2 }, 0)
    .to(titleWords, fadeInUpStagger, '>-0.8')
    .to('.o-fp-header__desc', fadeInUp, '>-0.3')
    .to('.o-fp-header__btn', fadeInUp, '>-0.3')
    .set(['.o-fp-header', '.o-fp-header__visual', '.o-fp-header__desc', '.o-fp-header__btn'], {
      clearProps: 'all',
    });

  appendInterfaceAnimation(tl);

  return tl;
}

// PAGE DEFAULT
function enterDefault({ app, mobile }) {
  initInterface();

  const header = document.querySelector('.o-page-header');
  if (!header) {
    const tl = gsap.timeline();
    tl.to(app, { opacity: 1, duration: 1, ease: 'power2.out' });
    appendInterfaceAnimation(tl);
    return tl;
  }

  const background = header.querySelector('.o-page-header__bg');
  const heightBackground = background.getBoundingClientRect().height;
  const title = document.querySelector('.o-page-header__title');
  const titleSplit = SplitText.create(title, {
    type: 'words',
    reduceWhiteSpace: false,
  });
  const titleWords = splitHyphenatedWords(titleSplit.words);
  const descParas = header.querySelectorAll('.o-page-header__desc .a-text p');
  const descSplit = descParas.length ? SplitText.create(descParas, { type: 'lines' }) : null;

  gsap.set(background, {
    clipPath: `inset(0px 0px ${heightBackground}px 0px round 10px)`,
  });
  gsap.set(titleWords, hiddenUp(40));
  const visual = header.querySelector('.o-page-header__visual');
  const visualImg = visual.querySelector('img');
  const visualHeight = visual.getBoundingClientRect().height;
  gsap.set(visual, { clipPath: `inset(${visualHeight}px 0px 0px 0px round 0px)` });
  gsap.set(visualImg, { scale: 1.4 });
  const button = header.querySelector('.o-page-header__btn');

  if (descSplit) gsap.set(descSplit.lines, hiddenUp(24));
  if (button) gsap.set(button, hiddenUp(24));

  const tl = gsap.timeline();

  tl.to(app, { opacity: 1, duration: 0.2 })
    .to(
      background,
      {
        clipPath: mobile
          ? 'inset(0px 0px 0px 0px round 0px)'
          : 'inset(20px 20px 20px 20px round 10px)',
        duration: 1.2,
        ease: easeCustom,
      },
      0
    )
    .to(titleWords, fadeInUpStagger, '>-1');

  if (descSplit) tl.to(descSplit.lines, fadeInUpStagger, '>-0.4');
  if (button) tl.to(button, fadeInUpStagger, '>-0.4');

  tl.to(
    '.o-page-header__visual',
    { clipPath: 'inset(0px 0px 0px 0px round 0px)', duration: 0.6, ease: easeCustom },
    '>-1'
  )
    .to(visualImg, { scale: 1, duration: 1 }, '<')
    .set([background, button, visual], {
      clearProps: 'all',
    });

  appendInterfaceAnimation(tl);

  return tl;
}

// HOME / ARCHIVE ACTION-UIMM
function enterHome({ app }) {
  initInterface();

  const header = document.querySelector('.t-home__header');
  const titleSplit = SplitText.create('.m-head-center__title', { type: 'words' });
  const titleWords = splitHyphenatedWords(titleSplit.words);
  const desc = header ? header.querySelector('.m-head-center__desc') : null;
  const dropdown = document.querySelector('.m-cat-dropdown');
  const cards = document.querySelectorAll('.o-posts-list__item');

  // Init ScrollTrigger stagger AVANT l'apparition pour que les y soient déjà corrects
  initPostsListStagger();

  gsap.set(titleWords, hiddenUp(40));
  if (desc) gsap.set(desc, hiddenUp());
  if (dropdown) gsap.set(dropdown, { autoAlpha: 0 });
  if (cards.length) gsap.set(cards, { autoAlpha: 0 });

  const tl = gsap.timeline();

  tl.to(app, { opacity: 1, duration: 0.2 }).to(titleWords, fadeInUpStagger, '>-0.1');

  if (desc) tl.to(desc, fadeInUp, '>-0.3');
  if (dropdown) tl.to(dropdown, { autoAlpha: 1, duration: 0.4, ease: easeCustom }, '>-0.2');

  if (cards.length) {
    tl.to(
      cards,
      { autoAlpha: 1, duration: 0.6, ease: easeCustom, stagger: { each: 0.1 } },
      '>-0.2'
    );
  }

  appendInterfaceAnimation(tl);

  return tl;
}

// ─── Registre des vues ───────────────────────────────────────────────────────
// Pour ajouter une vue : écrire une fonction enterXxx({ app, mobile })
// et l'enregistrer ici avec le nom du data-view correspondant.

const VIEW_ANIMATIONS = {
  'page-front-page': enterFrontPage,
  'page-home': enterHome,
  // 'page-contact': enterContact,
};

// ─── Exports ─────────────────────────────────────────────────────────────────

export function setupLinkTransitions() {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || !isInternalLink(link)) return;

    event.preventDefault();

    playLeaveAnimation().then(() => {
      window.location.href = link.href;
    });
  });
}

export async function initView() {
  const app = document.querySelector('#app');
  if (!app) return;

  initInterface();

  await document.fonts.ready;

  const view = app.dataset.view;
  const mobile = window.matchMedia('(max-width: 767px)').matches;

  const enterAnimation = VIEW_ANIMATIONS[view] ?? enterDefault;

  enterAnimation({ app, mobile });
}
